import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, Select, Button, Modal, Input } from "@/components/ui";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const TYPES = ["Comprehensive Health Assessment", "PHQ-9 Depression Screen", "GAD-7 Anxiety Screen", "HRSN Screening", "Risk Stratification", "Care Needs Screening"];

const OPTIONS = [["Not at all", 0], ["Several days", 1], ["More than half the days", 2], ["Nearly every day", 3]];
const QUESTIONS = {
  "PHQ-9 Depression Screen": [
    "Little interest or pleasure in doing things",
    "Feeling down, depressed, or hopeless",
    "Trouble falling/staying asleep, or sleeping too much",
    "Feeling tired or having little energy",
    "Poor appetite or overeating",
    "Feeling bad about yourself / that you are a failure",
    "Trouble concentrating on things",
    "Moving/speaking slowly, or being fidgety/restless",
    "Thoughts that you would be better off dead or of self-harm",
  ],
  "GAD-7 Anxiety Screen": [
    "Feeling nervous, anxious, or on edge",
    "Not being able to stop or control worrying",
    "Worrying too much about different things",
    "Trouble relaxing",
    "Being so restless that it is hard to sit still",
    "Becoming easily annoyed or irritable",
    "Feeling afraid as if something awful might happen",
  ],
};

function severityFor(type, total) {
  if (type === "PHQ-9 Depression Screen") return total >= 20 ? "Severe" : total >= 15 ? "Moderately Severe" : total >= 10 ? "Moderate" : total >= 5 ? "Mild" : "Minimal";
  if (type === "GAD-7 Anxiety Screen") return total >= 15 ? "Severe" : total >= 10 ? "Moderate" : total >= 5 ? "Mild" : "Minimal";
  return "";
}

export default function Assessments() {
  const [items, setItems] = useState(null);
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ member_id: "", type: TYPES[0], status: "In Progress", score: "" });
  const [answers, setAnswers] = useState({});

  const load = () => api.get("/assessments", { params: { status } }).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { api.get("/members").then((r) => setMembers(r.data)); }, []);

  const questions = QUESTIONS[form.type];
  const isScored = !!questions;
  const total = Object.values(answers).reduce((s, v) => s + v, 0);
  const allAnswered = isScored && Object.keys(answers).length === questions.length;

  const create = async () => {
    if (!form.member_id) return toast.error("Select a member");
    const member = members.find((m) => m.id === form.member_id);
    const payload = {
      member_id: form.member_id, member_name: `${member.first_name} ${member.last_name}`,
      type: form.type, status: form.status,
      score: form.score ? parseInt(form.score) : null,
      completed_date: form.status === "Completed" ? new Date().toISOString().slice(0, 10) : "",
      responses: [],
    };
    if (isScored) {
      if (!allAnswered) return toast.error("Please answer all items");
      payload.responses = questions.map((_, i) => ({ value: answers[i] ?? 0 }));
    }
    await api.post("/assessments", payload);
    toast.success(isScored ? `Assessment scored: ${total} (${severityFor(form.type, total)})` : "Assessment created");
    setOpen(false); setForm({ member_id: "", type: TYPES[0], status: "In Progress", score: "" }); setAnswers({}); load();
  };

  return (
    <div className="vc-fade-up">
      <PageHeader title="Assessments & Screenings" subtitle="Comprehensive health assessments, population screenings, and HRSN evaluations."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-assessment-btn"><Plus className="h-4 w-4" /> New Assessment</Button>} />

      <Card className="p-4 mb-4">
        <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="assess-filter">
          <option value="">All Statuses</option><option>Completed</option><option>In Progress</option>
        </Select>
      </Card>

      {items === null ? <Spinner /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Type</th><th className="px-4 py-3 font-semibold">Member</th>
                <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold">Score</th>
                <th className="px-4 py-3 font-semibold">Flag</th><th className="px-4 py-3 font-semibold">Assessor</th>
                <th className="px-4 py-3 font-semibold">Completed</th></tr></thead>
              <tbody className="divide-y divide-border">
                {items.map((a) => (
                  <tr key={a.id} className="hover:bg-muted transition-colors">
                    <td className="px-4 py-3 font-medium">{a.type}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.member_name}</td>
                    <td className="px-4 py-3"><Badge tone={a.status}>{a.status}</Badge></td>
                    <td className="px-4 py-3 font-mono">{a.score ?? "—"}</td>
                    <td className="px-4 py-3">{a.risk_flag ? <Badge tone="High">{a.risk_flag}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.assessor}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.completed_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Assessment" wide={isScored}>
        <div className="space-y-4">
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Member</label>
            <Select className="w-full mt-1.5" value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} data-testid="assess-member">
              <option value="">Select member…</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.mrn})</option>)}
            </Select></div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</label>
            <Select className="w-full mt-1.5" value={form.type} onChange={(e) => { setForm({ ...form, type: e.target.value }); setAnswers({}); }}>{TYPES.map((t) => <option key={t}>{t}</option>)}</Select></div>

          {isScored ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Over the last 2 weeks, how often…</p>
                <div className="text-sm">Score: <span className="font-mono font-bold text-brand-700" data-testid="assess-live-score">{total}</span> / {form.type.includes("PHQ") ? 27 : 21} · <span className="font-semibold">{allAnswered ? severityFor(form.type, total) : "—"}</span></div>
              </div>
              <div className="max-h-[46vh] overflow-y-auto space-y-2 pr-1">
                {questions.map((q, i) => (
                  <div key={i} className="rounded-md border border-border p-3">
                    <p className="text-sm mb-2">{i + 1}. {q}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                      {OPTIONS.map(([label, val]) => (
                        <button key={val} onClick={() => setAnswers({ ...answers, [i]: val })} data-testid={`assess-q${i}-${val}`}
                          className={`text-xs rounded px-2 py-1.5 border transition-colors ${answers[i] === val ? "border-brand-600 bg-brand-50 text-brand-800 font-semibold" : "border-border hover:bg-muted text-muted-foreground"}`}>
                          {label} ({val})
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
                <Select className="w-full mt-1.5" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option>In Progress</option><option>Completed</option></Select></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Score (optional)</label>
                <Input className="mt-1.5" type="number" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} /></div>
            </div>
          )}
          <Button className="w-full" onClick={create} data-testid="assess-save">{isScored ? "Score & Save Assessment" : "Create Assessment"}</Button>
        </div>
      </Modal>
    </div>
  );
}
